/**
 * ペルソナ一覧。
 *
 * ブランドが定義したもの（BRAND_DEFINED）が主役で、レビュアー属性から起こした
 * 参考値（DERIVED）は既定では混ぜない。混ぜるかどうかは画面で切り替える。
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  ClonePersonaFromDerivedDocument,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  PersonasDocument,
  Tag,
  formatJstDateTime,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";

import { type PersonaUserError } from "./PersonaForm";

export function PersonasPage() {
  const navigate = useNavigate();
  const [includeDerived, setIncludeDerived] = useState(false);
  const [result, refetch] = useQuery({
    query: PersonasDocument,
    variables: { includeDerived },
  });
  const [cloneState, clonePersona] = useMutation(
    ClonePersonaFromDerivedDocument,
  );
  // 複製元の id。開いている間だけ名前の入力欄を出す
  const [cloningId, setCloningId] = useState<string | null>(null);
  const [cloneName, setCloneName] = useState("");
  const [cloneErrors, setCloneErrors] = useState<PersonaUserError[]>([]);

  return (
    <div className="vd-page">
      <PageHeader
        title="ペルソナ"
        description="どんな人が、何を知りたくて商品を見にくるのか。ここで定義した観点で商品を採点します。"
        titleExtra={<HelpIcon slug="personas/list" />}
        extra={
          <span className="vd-row">
            <Link className="vd-button vd-button--primary" to="/personas/new">
              ペルソナを登録する
            </Link>
          </span>
        }
      />

      <div className="vd-stack">
        <Card title="表示">
          <label className="vd-row">
            <input
              type="checkbox"
              checked={includeDerived}
              onChange={(event) => setIncludeDerived(event.target.checked)}
            />
            レビュアー属性から起こした参考値も表示する
          </label>
          <p className="vd-card-note" style={{ marginBottom: 0 }}>
            参考値は実測から機械的に起こした人物像です。ブランドが定義したものとは
            別のものなので、採点の主役にはしません。
          </p>
        </Card>

        {result.error !== undefined ? (
          <ErrorState
            description={result.error.message}
            onRetry={() => refetch({ requestPolicy: "network-only" })}
          />
        ) : result.fetching && result.data === undefined ? (
          <LoadingState />
        ) : (result.data?.personas.length ?? 0) === 0 ? (
          <EmptyState
            title="まだペルソナがありません"
            description="1 件でも登録すると、その観点で商品を採点できるようになります。"
            action={
              <Link className="vd-button vd-button--primary" to="/personas/new">
                ペルソナを登録する
              </Link>
            }
          />
        ) : (
          (result.data?.personas ?? []).map((persona) => (
            <Card
              key={persona.id}
              title={
                <>
                  {persona.name}
                  {persona.origin === "DERIVED" ? (
                    <Tag tone="info">参考の初期値</Tag>
                  ) : null}
                </>
              }
              extra={
                persona.origin === "BRAND_DEFINED" ? (
                  <Link to={`/personas/${persona.id}`}>編集する</Link>
                ) : null
              }
              note={`版 ${persona.version} ・ 最終更新 ${formatJstDateTime(persona.updatedAt)}${
                persona.updatedBy === null
                  ? ""
                  : `（${persona.updatedBy.displayName}）`
              }`}
            >
              <p style={{ marginTop: 0 }}>{persona.description}</p>
              <div className="vd-row">
                {persona.attributes.map((attribute) => (
                  <Tag key={attribute}>{attribute}</Tag>
                ))}
              </div>
              {persona.barriers.length === 0 ? null : (
                <p className="vd-stat-note" style={{ marginBottom: 0 }}>
                  ためらい: {persona.barriers.join(" / ")}
                </p>
              )}
              {persona.questions.length === 0 ? null : (
                <p className="vd-stat-note" style={{ marginBottom: 0 }}>
                  知りたいこと: {persona.questions.length} 件
                </p>
              )}
              {persona.origin === "DERIVED" ? (
                <div className="vd-stack" style={{ marginTop: 8 }}>
                  <p className="vd-card-note" style={{ margin: 0 }}>
                    参考の初期値です。このままでは採点に使いません。複製すると、
                    ブランドが定義したペルソナとして編集できます。
                  </p>
                  {cloningId === persona.id ? (
                    <>
                      {cloneErrors.map((error) => (
                        <span
                          key={`${error.code}-${error.message}`}
                          className="vd-field-error"
                          role="alert"
                        >
                          {error.message}
                        </span>
                      ))}
                      <input
                        className="vd-input"
                        aria-label="複製して作るペルソナの名前"
                        placeholder="複製して作るペルソナの名前"
                        value={cloneName}
                        onChange={(event) => setCloneName(event.target.value)}
                      />
                      <div className="vd-row">
                        <Button
                          variant="primary"
                          disabled={
                            cloneState.fetching || cloneName.trim() === ""
                          }
                          onClick={() => {
                            void clonePersona({
                              input: {
                                sourcePersonaId: persona.id,
                                name: cloneName.trim(),
                              },
                            }).then((mutationResult) => {
                              if (mutationResult.error !== undefined) {
                                setCloneErrors([
                                  {
                                    field: null,
                                    code: "INTERNAL_ERROR",
                                    message: `複製できませんでした: ${mutationResult.error.message}`,
                                  },
                                ]);
                                return;
                              }
                              const payload =
                                mutationResult.data?.clonePersonaFromDerived;
                              if (payload === undefined) {
                                return;
                              }
                              if (payload.userErrors.length > 0) {
                                setCloneErrors([...payload.userErrors]);
                                return;
                              }
                              setCloneErrors([]);
                              setCloningId(null);
                              setCloneName("");
                              const created = payload.persona;
                              if (created !== null && created !== undefined) {
                                navigate(`/personas/${created.id}`);
                              }
                            });
                          }}
                        >
                          複製する
                        </Button>
                        <Button
                          disabled={cloneState.fetching}
                          onClick={() => {
                            setCloningId(null);
                            setCloneErrors([]);
                          }}
                        >
                          やめる
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="vd-row">
                      <Button
                        aria-label={`「${persona.name}」を複製する`}
                        onClick={() => {
                          setCloningId(persona.id);
                          setCloneName(`${persona.name}（複製）`);
                          setCloneErrors([]);
                        }}
                      >
                        複製する
                      </Button>
                    </div>
                  )}
                </div>
              ) : null}
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
